import React, { useRef, useEffect, useState } from "react";
import { select, axisBottom, axisLeft, scaleLinear, scaleBand, descending } from "d3";
import { useResizeObserver } from "../../utility/useResizeObserver";
import "d3-transition";
import { fixCountryName } from "../../utility/formatCountry";

export const BarChart = ({ data, year, property, setTitle, country, hidden }) => {
    // We'll use two refs: one for the bars/y-axis ("barArea"), one for the pinned x-axis ("xAxisArea").
    const barRef = useRef();
    const xAxisRef = useRef();

    // This is for measuring the container (we'll keep your existing dimensions logic).
    // We'll do the main scrolling in a child <div>, but still measure the parent wrapper:
    const wrapperRef = useRef();
    const dimensions = useResizeObserver(wrapperRef);
    const margin = { top: 10, right: 100, bottom: 0, left: 150 };

    // Size of bars
    const BAR_HEIGHT = 20;
    const BAR_SPACING = 5;

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryArray, setCategoryArray] = useState([]);
    const [chartHeight, setChartHeight] = useState(0);

    // State for the chart title.
    const [barLabel, setBarLabel] = useState("");
    const [subtitle, setSubtitle] = useState(false);
    // State for whether the chart scroll area is scrollable.
    const [isScrollable, setIsScrollable] = useState(false);

    const propertiesWithCategories = [
        "Nominal Capacity (MWt)",
        "Energy used (net) (TJ/year)",
        "Installed Capacity (MW)",
        "Gross Energy Produced (GEP) (GWh/year)",
    ];

    useEffect(() => {
        // Same logic for discovering typologies
        const allCategories = new Set();
        data.forEach((d) => {
            Object.keys(d).forEach((key) => {
                if (key !== "Country" && key !== "Year") {
                    allCategories.add(key);
                }
            });
        });
        const categories = Array.from(allCategories);
        // Prepend "All" so the user can sum across all keys.
        setCategoryArray(["All Categories", ...categories]);
        if (!selectedCategory || !allCategories.has(selectedCategory)) {
            setSelectedCategory("All Categories");
        }
    }, [data]);

    // Check if the scrollable area is scrollable.
    useEffect(() => {
        const chartScrollEl = document.getElementById("chartScroll");
        if (chartScrollEl) {
            setIsScrollable(chartScrollEl.scrollHeight > chartScrollEl.clientHeight);
        }
    }, [data, dimensions]);

    // Same rounding function
    const roundedRectPath = (x, y, width, height, radius) => {
        const r = Math.min(radius, width / 2, height / 2);
        return (
            `M${x},${y + r}
       a${r},${r} 0 0 1 ${r},-${r}
       h${width - 2 * r}
       a${r},${r} 0 0 1 ${r},${r}
       v${height - 2 * r}
       a${r},${r} 0 0 1 -${r},${r}
       h-${width - 2 * r}
       a${r},${r} 0 0 1 -${r},-${r}
       Z`
        );
    };

    // We'll separate the "drawing bars + y-axis" from "drawing the pinned x-axis".
    // We'll call these inside one big useEffect, so we only compute scales once.

    useEffect(() => {
        if (!dimensions || !selectedCategory) return;

        const { width, height } = dimensions;

        // Process data: if "All" is selected, sum across all keys (except "Country" and "Year").
        const processedData = data.map((d) => {
            if (selectedCategory === "All Categories") {
                let sum = 0;
                Object.keys(d).forEach((key) => {
                    if (key !== "Country" && key !== "Year") {
                        const val = parseFloat(d[key]);
                        if (!isNaN(val)) sum += val;
                    }
                });
                return { ...d, "All Categories": sum };
            }
            return d;
        });

        // Filter + sort data
        const filteredData = processedData.filter((d) => {
            const value = d[selectedCategory];
            return value != null && value !== "" && !isNaN(value) && parseFloat(value) !== 0;
        });
        filteredData.sort((a, b) => descending(a[selectedCategory], b[selectedCategory]));

        const allValuesInvalid = filteredData.every((d) => +d[selectedCategory] === 0);
        if (!filteredData.length || allValuesInvalid) {
            select(barRef.current).selectAll("*").remove();
            select(barRef.current)
                .attr("width", width)
                .attr("height", 80)
                .append("text")
                .attr("x", width / 2)
                .attr("y", 40)
                .attr("text-anchor", "middle")
                .attr("fill", "red")
                .text("No data available");

            // Clear pinned x-axis
            select(xAxisRef.current).selectAll("*").remove();
            setBarLabel("");
            return;
        }

        // 3) Sort data descending
        filteredData.sort((a, b) => descending(a[selectedCategory], b[selectedCategory]));

        // 4) Compute total "virtual" height for the bars
        const totalBars = filteredData.length;
        const totalChartHeight = totalBars * (BAR_HEIGHT + BAR_SPACING);
        const chartHeight = totalChartHeight + margin.top + margin.bottom;
        const chartInnerWidth = width - margin.left - margin.right;

        setChartHeight(totalChartHeight + 100);


        // 5) Build Scales
        const values = filteredData.map((d) => +d[selectedCategory]);
        const xMax = Math.max(...values);
        const xScale = scaleLinear().domain([0, xMax]).range([0, chartInnerWidth]).nice();

        const yScale = scaleBand()
            .domain(filteredData.map((d) => d.Country))
            .range([0, totalChartHeight]) // total space for bars (no margin included)
            .padding(0);

        // Set a dynamic title based on the property and selected category.
        const newBarLabel = getDynamicBarTitle(selectedCategory, property, year, country, setSubtitle);
        setBarLabel(newBarLabel);
        setTitle(newBarLabel);

        // 6) Draw bars + y-axis in the "barArea" <svg>
        drawBarsAndYAxis({
            barRef,
            filteredData,
            xScale,
            yScale,
            margin,
            chartWidth: width,
            totalChartHeight,
            chartHeight,
            BAR_HEIGHT,
            BAR_SPACING,
            roundedRectPath,
            selectedCategory,
            year,
            setTitle,
            updateScrollable: (scrollable) => setIsScrollable(scrollable),
        });

        // 7) Draw pinned x-axis in the "xAxisArea" <svg>
        drawPinnedXAxis({
            xAxisRef,
            xScale,
            margin,
            chartWidth: width,
        });
    }, [data, dimensions, selectedCategory]);

    // If you still need a dynamic <select> for categories
    return (
        <>
            <div className="chartTitle">
                <p id={!propertiesWithCategories.includes(property) ? "noCategoriesTitle" : ''}>{barLabel}</p>
                {subtitle && (<p id="subtitle">Please note that the following values refer to the projects and plants described in detail in the WGC reporting. They may overlook project data and fail to achieve the actual Grand Total values</p>)}
                {propertiesWithCategories.includes(property) && categoryArray.length > 0 && (
                    <select
                        className="input"
                        id="dataCategoriesBar"
                        value={selectedCategory || ""}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        {categoryArray.map((category, index) => (
                            <option key={index} value={category}>
                                {category}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            <div
                ref={wrapperRef}
                id="chartContainer"
                style={{ position: "relative", height: hidden ? chartHeight : "100%" }}
            >
                <div
                    id="chartScroll"
                    style={{
                        position: "absolute",
                        top: hidden ? "0px" : "90px",
                        left: 0,
                        right: 0,
                        bottom: hidden ? "40px" : "30px",
                        overflowY: hidden ? "visible" : "auto"
                    }}
                >
                    <svg ref={barRef} className="chartSvg" />
                </div>

                <svg
                    ref={xAxisRef}
                    id="xAxisArea"
                    style={{
                        position: "absolute",
                        bottom: "0px",
                        left: 0,
                        right: 0,
                        height: "40px"
                    }}
                />
                {isScrollable && !hidden && (
                    <div className="scrollHint">Scroll ↓</div>
                )}
            </div>
        </>
    );
};

/** Draw the bars + y-axis into the scrollable <svg> (barRef). */
function drawBarsAndYAxis({
    barRef,
    filteredData,
    xScale,
    yScale,
    margin,
    chartWidth,
    totalChartHeight,
    BAR_HEIGHT,
    BAR_SPACING,
    roundedRectPath,
    selectedCategory,
    year,
    setTitle,
    updateScrollable
}) {
    // 1) measure #chartScroll to see how tall the scroll region is
    const scrollEl = document.getElementById("chartScroll");
    const scrollHeight = scrollEl?.clientHeight || 0;

    // We'll also keep in mind we have margin.top + margin.bottom in the final chart
    const chartHeight = totalChartHeight + margin.top + margin.bottom;

    // 2) If chartHeight < scrollHeight, compute an offset so it "pins" the bars at the bottom
    //    Actually, we want to align near the bottom if there's empty space.
    //    We'll do offset = max(0, scrollElHeight - chartHeight).
    const offset = Math.max(0, scrollHeight - chartHeight);

    // If you want a small extra gap between the bottom bar and the pinned axis, 
    // you could do offset = Math.max(0, (scrollHeight - chartHeight) - 10);

    const svg = select(barRef.current)
        .attr("width", chartWidth)
        .attr("height", chartHeight);

    svg.selectAll("*").remove();

    const graphArea = svg
        .append("g")
        .attr(
            "transform",
            `translate(${margin.left}, ${margin.top + offset})`
        );

    // ========== Build Gradients ==========
    const values = filteredData.map((d) => +d[selectedCategory]);
    const maxVal = Math.max(...values);

    svg.select("defs")?.remove();
    const defs = svg.append("defs");

    filteredData.forEach((d, i) => {
        const val = +d[selectedCategory] || 0;
        const ratio = val / maxVal;

        const gradient = defs
            .append("linearGradient")
            .attr("id", `gradient-${i}`)
            .attr("x1", "0%")
            .attr("x2", "100%")
            .attr("y1", "0%")
            .attr("y2", "0%");

        gradient
            .append("stop")
            .attr("offset", `${(1 - ratio) * 100}%`)
            .attr("stop-color", "#32c737");

        gradient
            .append("stop")
            .attr("offset", "100%")
            .attr("stop-color", "#FF7829");
    });

    const animationDuration = 1000;
    const getBarY = (country) => yScale(country) + BAR_SPACING / 2;
    const getBarHeight = () => yScale.bandwidth() - BAR_SPACING;

    // BARS
    const bars = graphArea.selectAll(".bar").data(filteredData, (d) => d.Country);

    // Enter
    bars
        .enter()
        .append("path")
        .attr("class", "bar")
        .attr("d", (d, i) => {
            const barY = getBarY(d.Country);
            const barH = getBarHeight();
            return roundedRectPath(0, barY, 0, barH, 5);
        })
        .attr("fill", (d, i) => `url(#gradient-${i})`)
        .transition()
        .duration(animationDuration)
        .attr("d", (d) => {
            const barY = getBarY(d.Country);
            const barH = getBarHeight();
            const barW = xScale(d[selectedCategory]);
            return roundedRectPath(0, barY, barW, barH, 5);
        })
        .on("end", function (event, d) {
            // Hover
            select(this)
                .on("mouseover", function (evt, dd) {
                    graphArea.selectAll(".bar").transition().duration(250).style("opacity", 0.5);
                    select(this).transition().duration(250).style("opacity", 1);

                    graphArea
                        .selectAll(".label")
                        .filter((lbl) => lbl.Country === dd.Country)
                        .transition()
                        .duration(250)
                        .style("opacity", 1);
                })
                .on("mouseout", function (evt, dd) {
                    graphArea.selectAll(".bar").transition().duration(250).style("opacity", 1);
                    graphArea
                        .selectAll(".label")
                        .filter((lbl) => lbl.Country === dd.Country)
                        .transition()
                        .duration(250)
                        .style("opacity", 0.75);
                });
        });

    // Update
    bars
        .transition()
        .duration(animationDuration)
        .attr("d", (d, i) => {
            const barY = getBarY(d.Country);
            const barH = getBarHeight();
            const barW = xScale(d[selectedCategory]);
            return roundedRectPath(0, barY, barW, barH, 5);
        })
        .attr("fill", (d, i) => `url(#gradient-${i})`)
        .on("end", function (event, d) {
            select(this)
                .on("mouseover", function (evt, dd) {
                    graphArea.selectAll(".bar").transition().duration(250).style("opacity", 0.5);
                    select(this).transition().duration(250).style("opacity", 1);

                    graphArea
                        .selectAll(".label")
                        .filter((lbl) => lbl.Country === dd.Country)
                        .transition()
                        .duration(250)
                        .style("opacity", 1);
                })
                .on("mouseout", function (evt, dd) {
                    graphArea.selectAll(".bar").transition().duration(250).style("opacity", 1);
                    graphArea
                        .selectAll(".label")
                        .filter((lbl) => lbl.Country === dd.Country)
                        .transition()
                        .duration(250)
                        .style("opacity", 0.75);
                });
        });

    // Exit
    bars
        .exit()
        .transition()
        .duration(animationDuration)
        .attr("d", (d) => {
            const barY = getBarY(d.Country);
            const barH = getBarHeight();
            return roundedRectPath(0, barY, 0, barH, 5);
        })
        .remove();

    // GRID LINES (vertical)
    let gridlines = graphArea.select(".gridlines");
    if (gridlines.empty()) {
        gridlines = graphArea.append("g").attr("class", "gridlines");
    }

    const xTicks = xScale.ticks(10);
    const gridSelection = gridlines.selectAll(".vertical-grid").data(xTicks);

    gridSelection.exit().remove();
    gridSelection
        .enter()
        .append("line")
        .attr("class", "vertical-grid")
        .attr("y1", 0)
        .attr("y2", yScale.range()[1]) // totalChartHeight
        .attr("x1", (d) => xScale(d))
        .attr("x2", (d) => xScale(d))
        .style("stroke", "#000")
        .style("stroke-width", 0.2)
        .style("stroke-dasharray", "3 3");

    gridSelection
        .attr("y1", 0)
        .attr("y2", yScale.range()[1])
        .transition()
        .duration(500)
        .attr("x1", (d) => xScale(d))
        .attr("x2", (d) => xScale(d));

    // Numeric Labels
    const labels = graphArea.selectAll(".label").data(filteredData, (d) => d.Country);

    labels
        .exit()
        .transition()
        .duration(500)
        .attr("x", 0)
        .remove();

    labels
        .transition()
        .duration(500)
        .attr("x", (d) => xScale(d[selectedCategory]) + 5)
        .attr("y", (d) => getBarY(d.Country) + getBarHeight() / 2)
        .text((d) => (+d[selectedCategory]).toFixed(2));

    labels
        .enter()
        .append("text")
        .attr("class", "label")
        .attr("x", 0)
        .attr("y", (d) => getBarY(d.Country) + getBarHeight() / 2)
        .attr("text-anchor", "start")
        .style("alignment-baseline", "middle")
        .style("font-size", "10px")
        .style("fill", "#000")
        .style("opacity", 0.75)
        .text((d) => (+d[selectedCategory]).toFixed(2))
        .transition()
        .duration(1000)
        .attr("x", (d) => xScale(d[selectedCategory]) + 5);

    // Y Axis only (no X axis here)
    let yAxisGroup = graphArea.select(".y-axis");
    if (yAxisGroup.empty()) {
        yAxisGroup = graphArea.append("g").attr("class", "y-axis");
    }
    yAxisGroup.call(axisLeft(yScale).tickSize(0));

    // Remove built-in text for y-axis so we can do custom HTML
    yAxisGroup.selectAll("text").style("opacity", 0);

    // Remove old container
    select("#chartScroll").select(".y-axis-labels").remove();

    const labelContainer = select("#chartScroll")
        .append("div")
        .attr("class", "y-axis-labels")
        .style("position", "absolute")
        // top = margin.top + offset
        .style("top", `${margin.top + offset}px`)
        .style("left", `0 px`)
        .style("width", `${margin.left}px`)
        .style("height", `${totalChartHeight}px`)
        .style("overflow", "hidden");

    labelContainer
        .selectAll(".y-label")
        .data(filteredData.map((d) => d.Country))
        .enter()
        .append("div")
        .attr("class", "y-label")
        .style("position", "absolute")
        .style("left", "0px")
        .style("text-align", "right")
        .style("font-size", "10px")
        .style("white-space", "normal")
        .style("word-wrap", "break-word")
        .style("width", "90%")
        .style("transform", "translateY(-50%)")
        .style("top", (d) => {
            const barTop = getBarY(d) + getBarHeight() / 2; // Middle
            return `${barTop}px`;
        })
        .html((d) => fixCountryName(d));

    // After drawing, update scrollability.
    if (updateScrollable) {
        const chartScrollEl = document.getElementById("chartScroll");
        const needsScroll = chartScrollEl && chartScrollEl.scrollHeight > chartScrollEl.clientHeight;
        updateScrollable(needsScroll);
    }
}

/** Draw only the pinned X-axis in xAxisArea */
function drawPinnedXAxis({ xAxisRef, xScale, margin, chartWidth }) {
    const svgX = select(xAxisRef.current)
        .attr("width", chartWidth)
        // We'll let the parent <svg> define its height in CSS (50px).
        .attr("height", 50);

    svgX.selectAll("*").remove();

    // We'll place the axis at the bottom of this 50px region
    const axisGroup = svgX
        .append("g")
        .attr("transform", `translate(${margin.left}, 10)`);
    // If you want it truly at bottom, you can do: 
    // .attr("transform", `translate(${margin.left}, 45)`); 
    // adjusting for any label/ticks.

    const xAxis = axisBottom(xScale).ticks(10);
    axisGroup.call(xAxis);

    // Optionally add an axis label
    axisGroup
        .append("text")
        .attr("fill", "#FF7829")
        .attr("x", (chartWidth - margin.left - margin.right) / 2)
        .attr("y", 35) // slightly below the axis
        .attr("text-anchor", "middle")
        .text("Value"); // or a dynamic label if you want
}

// Helper: Generate a dynamic title based on the selected category, the original property, and the year.
// For example, if the property is a power value, it starts with "Power -"; if H&C, with "H&C -", and
// if the category "All" is selected, it prepends "Total".
function getDynamicBarTitle(selectedCategory, property, year, country, setSubtitle) {
    const powerProps = ["Installed Capacity (MW)", "Gross Energy Produced (GEP) (GWh/year)"];
    const hcProps = ["Nominal Capacity (MWt)", "Energy used (net) (TJ/year)"];
    // Build a country/region string.
    const countryTitle =
        country
            .map((countryName, index) =>
                countryName.label + (index === country.length - 1 ? '' : ', ')
            )
            .join('');

    switch (property) {
        case 'Bar':
            setSubtitle(false);
            return `Total Gross Electrical Generation GWh/year in ${countryTitle} for WGC ${year}`;
        case 'Geothermal Gross Electrical generation GWh/yr':
            setSubtitle(false);
            return ` Total Gross Electrical Generation GWh/year in ${countryTitle} for WGC ${year}`;
        case 'Total Gross Electrical generation GWh/yr':
            setSubtitle(false);
            return `Total Gross Electrical Generation GWh/year in ${countryTitle} for WGC ${year}`;
        case 'Total Installed Capacity (MWe)':
            setSubtitle(false);
            return `Total Installed Capacity (MWe) in ${countryTitle} for WGC ${year}`;
        case 'Total Energy used (TJ/year)':
            setSubtitle(false);
            return `Total Energy Used (TJ/year) ${countryTitle} for WGC ${year}`;
        case 'Total Installed Capacity (MWt)':
            setSubtitle(false);
            return `Total Installed Capacity (MWt) in ${countryTitle} for WGC ${year}`;
        case 'Gross Energy Produced (GEP) (GWh/year)':
            setSubtitle(true);
            return `Gross Energy Produced  of ${selectedCategory} as declared for geothermal power plants (GWh/year) in ${countryTitle} for WGC ${year}`;
        case 'Installed Capacity (MW)':
            setSubtitle(true);
            return `Installed Capacity of ${selectedCategory} as declared for geothermal power plants (MWt) in ${countryTitle} for WGC ${year}`;
        case 'Energy used (net) (TJ/year)':
            setSubtitle(true);
            return `Energy Used (TJ/year) of ${selectedCategory} as declared for geothermal Heating and Cooling categories in ${countryTitle} for WGC ${year}`;
        case 'Nominal Capacity (MWt)':
            setSubtitle(true);
            return `Installed Capacity (MWt) of ${selectedCategory} as declared for geothermal Heating and Cooling categories in ${countryTitle} for WGC ${year}`;
    }

    return `${property} in ${countryTitle} for WGC ${year}`;
}